import React from 'react';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import { consultAgent, sendConversationMessage, setSpendConfirm } from '../../services/conversationService';
import type { ChatSession, MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

interface Props {
  session: ChatSession;
  myUid?: string;
  members: MemberSpec[];
  memberNames: string[];
  catalogAvatars: Record<string, string>;
  isOwner?: boolean;
  responding: MemberSpec[];
  sending?: boolean;
  repliedSince: Set<string>;
  endRef: React.RefObject<HTMLDivElement>;
  onReact?: (messageId: string, emoji: string) => void;
  onBookmark?: (messageId: string, bookmarked: boolean) => void;
  onDeleteMessage?: (messageId: string) => void;
  onWalletChanged?: () => void;
  onDownloadArtifact?: (artifactId: string) => void;
  onSend: (text: string, targets?: string[]) => void;
  onAddAgent: (agentId: string) => Promise<void>;
  onSelectAgent: (member: MemberSpec) => void;
  onMention: (name: string) => void;
}

/**
 * MessageStream — the scrolling list of message bubbles, split out of ChatThread and MEMOIZED so that typing in
 * the composer (which updates ChatThread's local `text` state on every keystroke) NO LONGER re-renders + re-parses
 * every bubble (Markdown, reactions, artifacts). It re-renders only when the messages/members/turn-state actually
 * change. ChatThread stabilizes the props it passes (useMemo/useCallback) so the memo holds across keystrokes.
 * The nearest prior user prompt for each message is precomputed in ONE pass here (was an O(n) find per bubble).
 */
const MessageStream: React.FC<Props> = ({
  session, myUid, members, memberNames, catalogAvatars, isOwner, responding, sending, repliedSince, endRef,
  onReact, onBookmark, onDeleteMessage, onWalletChanged, onDownloadArtifact, onSend, onAddAgent, onSelectAgent, onMention,
}) => {
  // Precompute, in a single pass, the nearest PRIOR "you" message content for each index (the regenerate prompt).
  const promptTexts: (string | undefined)[] = [];
  let lastYou: string | undefined;
  for (const m of session.messages) {
    promptTexts.push(lastYou);
    if (m.author === 'you') lastYou = m.content;
  }
  return (
    <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 space-y-5">
      {session.messages.map((m, i) => (
        <MessageBubble
          key={m.id}
          msg={m}
          myUid={myUid}
          conversationId={session.id}
          promptText={promptTexts[i]}
          member={members.find((mm) => mm.id === m.author) || null}
          avatarImage={(() => { const pid = members.find((mm) => mm.id === m.author)?.profile_id; return pid ? catalogAvatars[pid] : null; })()}
          onSelectAgent={onSelectAgent}
          memberNames={memberNames}
          onMention={onMention}
          onReact={onReact && !m.id.startsWith('temp-') ? (emoji) => onReact(m.id, emoji) : undefined}
          onBookmark={isOwner && onBookmark && m.author !== 'you' && m.author !== 'system' && !m.id.startsWith('temp-') ? (b) => onBookmark(m.id, b) : undefined}
          onDelete={isOwner && onDeleteMessage && m.author !== 'you' && m.author !== 'system' && !m.id.startsWith('temp-') ? () => onDeleteMessage(m.id) : undefined}
          onAddAgent={isOwner ? onAddAgent : undefined}
          onConsultAgent={async (agentId) => { await consultAgent(session.id, agentId); }}
          onRegenerate={async (prompt) => {
            // Re-run the prompt for a fresh variation — silent (no re-post); the spend-confirm
            // gate shows the cost before it charges.
            await sendConversationMessage(session.id, prompt, undefined, undefined, undefined, { silent: true });
          }}
          onConfirmSpend={async (spec, dontAskAgain, edits) => {
            if (dontAskAgain) { try { await setSpendConfirm(false); } catch { /* best-effort */ } }
            // Resume the gated generation: confirmed + silent (no duplicate user message). We re-send
            // the ORIGINAL message (spec.prompt) so classification is stable, and carry the user's
            // approved/edited preview text so we generate exactly what they saw. Streams in via onSnapshot.
            await sendConversationMessage(session.id, spec.prompt, undefined, undefined, undefined, {
              confirmed: true, silent: true, musicStyle: spec.musicStyle, musicKind: spec.musicKind, mediaStyle: spec.mediaStyle, cameraMove: spec.cameraMove, mediaAspect: spec.aspect, mediaSize: spec.size, durationSec: spec.durationSec,
              previewPrompt: edits.previewPrompt, previewLyrics: edits.previewLyrics,
            });
            onWalletChanged?.(); // the spend just happened → refresh the balance meter
          }}
          onAction={(action, value) => {
            if (action === 'artifact:download') { if (value) onDownloadArtifact?.(value); return; }
            if (value) onSend(value, m.author !== 'you' ? [m.author] : undefined);
          }}
        />
      ))}
      {sending && responding.length > 0 && <TypingIndicator members={responding} />}
      {sending && responding.length === 0 && repliedSince.size === 0 && (
        <p className={`${TYPE.control} text-secondary-400 pl-11`} data-testid="generic-typing">typing…</p>
      )}
      <div ref={endRef} />
    </div>
  );
};

export default React.memo(MessageStream);
